import type { BrandingPublic } from '@accounts/shared';
import { useQuery } from '@tanstack/react-query';

import { getBranding } from './api';
import { queryKeys } from './queryKeys';

/**
 * R2 — BRANDING IS NEVER HARD-CODED. The product name, tagline, supporting line, glyph and
 * logo all come from `GET /api/branding`, which is public precisely so the signed-out
 * sign-in page can render them. No surface in this app spells the product's name itself.
 *
 * The endpoint is cheap and read-live on the server, so it is cached for the session and
 * invalidated by the one screen that can change it.
 */
export function useBranding() {
  return useQuery({
    queryKey: queryKeys.branding,
    queryFn: getBranding,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The single letter shown oversized on the sign-in panel and in the rail's brand mark.
 * Falls back to the first letter of the product name, exactly as the design does — the
 * server requires 1–2 characters, so this is also what the settings form submits when the
 * field is left empty.
 */
export function brandGlyph(branding: Pick<BrandingPublic, 'glyph' | 'name'>): string {
  const glyph = branding.glyph.trim();
  if (glyph !== '') return glyph;
  const name = branding.name.trim();
  return name === '' ? '' : (name.charAt(0).toUpperCase() ?? '');
}
